import React from "react";
import {
  Home,
  Package,
  Hotel,
  MessageSquare,
  BarChart,
  Building2,
  CreditCard,
} from "lucide-react";
import {
  routeConfig,
  generateNavigation,
  filterRoutesByPermission,
} from "../routes";
import { AdminNavLinkConfig } from "../adminNavigationConfig";

export interface NavigationGroup {
  id: string;
  titleKey: string;
  defaultTitle: string;
  icon: React.ReactNode;
  items: AdminNavLinkConfig[];
}

/**
 * Create navigation groups from route configuration
 * This maintains compatibility with the existing group structure
 */
export const createNavigationGroupsFromRoutes = (
  userPermissions: string[],
): NavigationGroup[] => {
  // Filter routes by permissions
  const filteredRoutes = filterRoutesByPermission(
    routeConfig.admin.routes,
    userPermissions,
  );

  // Generate navigation items
  const navItems = generateNavigation(
    filteredRoutes,
    routeConfig.admin.basePath,
  );

  // Convert to AdminNavLinkConfig format
  const navLinksConfig: AdminNavLinkConfig[] = navItems.map((item) => ({
    to: item.to,
    permission: item.permission || "",
    icon: item.icon || null,
    textKey: item.textKey,
    defaultText: item.defaultText,
    notificationCountKey: item.notificationCountKey,
  }));

  // Create a map for easy lookup
  const linkMap = new Map<string, AdminNavLinkConfig>();
  navLinksConfig.forEach((link) => {
    linkMap.set(link.to, link);
  });

  // Build the Orders item with nested children
  const ordersItem = linkMap.get("/admin/requests");
  const ordersChildren = [
    linkMap.get("/admin/orders"),
    linkMap.get("/admin/service-requests"),
    linkMap.get("/admin/late-checkout"),
    linkMap.get("/admin/transfer-requests"),
  ].filter(Boolean) as AdminNavLinkConfig[];

  // Create the Orders item with children attached
  const ordersWithChildren: AdminNavLinkConfig | undefined = ordersItem
    ? {
        ...ordersItem,
        children: ordersChildren.length > 0 ? ordersChildren : undefined,
      }
    : undefined;

  // Group definitions matching the route metadata groups
  return [
    {
      id: "core",
      titleKey: "admin_group_core",
      defaultTitle: "Core",
      icon: <Home className="h-4 w-4" />,
      items: [
        linkMap.get("/admin/dashboard"),
        linkMap.get("/admin/queue"),
        linkMap.get("/admin/front-desk"),
      ].filter(Boolean) as AdminNavLinkConfig[],
    },
    {
      id: "operations",
      titleKey: "admin_group_operations",
      defaultTitle: "Operations",
      icon: <Package className="h-4 w-4" />,
      items: [
        ordersWithChildren,
        linkMap.get("/admin/kitchen-display"),
        // PMS Outbox page (operations visibility)
        linkMap.get("/admin/pms-outbox"),
      ].filter(Boolean) as AdminNavLinkConfig[],
    },
    {
      id: "room-management",
      titleKey: "admin_group_room_management",
      defaultTitle: "Room Management",
      icon: <Hotel className="h-4 w-4" />,
      items: [
        linkMap.get("/admin/rooms"),
        linkMap.get("/admin/reservations"),
        linkMap.get("/admin/guests"),
        linkMap.get("/admin/housekeeping"),
      ].filter(Boolean) as AdminNavLinkConfig[],
    },
    {
      id: "communication",
      titleKey: "admin_group_communication",
      defaultTitle: "Communication",
      icon: <MessageSquare className="h-4 w-4" />,
      items: [linkMap.get("/admin/chat")].filter(
        Boolean,
      ) as AdminNavLinkConfig[],
    },
    {
      id: "setup",
      titleKey: "admin_group_setup",
      defaultTitle: "Setup & Administration",
      icon: <Building2 className="h-4 w-4" />,
      items: [
        linkMap.get("/admin/menu"),
        linkMap.get("/admin/service-types"),
        linkMap.get("/admin/transfer-destinations"),
        linkMap.get("/admin/late-checkout-settings"),
        linkMap.get("/admin/guest-guide"),
        linkMap.get("/admin/wifi"),
        linkMap.get("/admin/lock-connections"),
        linkMap.get("/admin/activities"),
        linkMap.get("/admin/workforce"),
        linkMap.get("/admin/user-management"),
        linkMap.get("/admin/customer-payments"),
      ].filter(Boolean) as AdminNavLinkConfig[],
    },
    {
      id: "analytics",
      titleKey: "admin_group_analytics",
      defaultTitle: "Analytics",
      icon: <BarChart className="h-4 w-4" />,
      items: [
        linkMap.get("/admin/statistics"),
        linkMap.get("/admin/audit-log"),
      ].filter(Boolean) as AdminNavLinkConfig[],
    },
    {
      id: "billing",
      titleKey: "admin_group_billing",
      defaultTitle: "Billing",
      icon: <CreditCard className="h-4 w-4" />,
      items: [
        linkMap.get("/admin/billing"),
        linkMap.get("/admin/revenue"),
        linkMap.get("/admin/invoices"),
      ].filter(Boolean) as AdminNavLinkConfig[],
    },
  ];
};

// Default expanded groups (you can save this to localStorage)
export const DEFAULT_EXPANDED_GROUPS = ["core", "operations"];

// Helper to get all items from groups
export const getAllItemsFromGroups = (
  groups: NavigationGroup[],
): AdminNavLinkConfig[] => {
  return groups.flatMap((group) => group.items);
};
